import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import type { BulkHardDeleteResult } from '@/lib/api/bulk';

/**
 * Compact ticks for table rows.
 *
 * Not the Checkbox component: that one is a padded, bordered card built for
 * forms, and cn() is a plain join rather than a tailwind merge, so overriding
 * its padding from a caller is a coin toss over which rule the stylesheet
 * happens to emit last. A bare input matches what RoleMatrixEditor and
 * EventAccessSection already do in tight spaces.
 */

export interface RowCheckboxProps {
  checked: boolean;
  onChange: () => void;
  /** Names the row, e.g. `Pilih Amoxicillin 500mg`. Read aloud, so keep it specific. */
  label: string;
  disabled?: boolean;
}

export function RowCheckbox({ checked, onChange, label, disabled }: RowCheckboxProps) {
  return (
    <input
      type="checkbox"
      className="accent-primary cursor-pointer disabled:cursor-not-allowed"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
    />
  );
}

export interface SelectAllCheckboxProps {
  checked: boolean;
  /** Some visible rows ticked, but not all. Shown as a dash. */
  indeterminate: boolean;
  onChange: () => void;
  label?: string;
  disabled?: boolean;
}

export function SelectAllCheckbox({
  checked,
  indeterminate,
  onChange,
  label = 'Pilih semua baris yang tampil',
  disabled,
}: SelectAllCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  // indeterminate is a DOM property with no HTML attribute, so React cannot set
  // it and it has to go on the node by hand.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="accent-primary cursor-pointer disabled:cursor-not-allowed"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
    />
  );
}

export interface BulkActionBarProps {
  count: number;
  /** Ticked rows the filter is hiding. Stated plainly rather than quietly acted on. */
  hiddenCount?: number;
  /** What is being counted: 'produk', 'pelanggan'. */
  noun: string;
  onClear: () => void;
  children: ReactNode;
}

export function BulkActionBar({
  count,
  hiddenCount = 0,
  noun,
  onClear,
  children,
}: BulkActionBarProps) {
  if (count === 0) return null;
  return (
    <div
      role="region"
      aria-label={`Aksi untuk ${noun} terpilih`}
      className="px-4 py-3 border-b border-line bg-primary/5 flex flex-wrap items-center gap-3"
    >
      <p className="text-small font-medium text-ink">
        {count} {noun} dipilih
        {hiddenCount > 0 && (
          <span className="font-normal text-ink-subtle">
            {' '}
            — {hiddenCount} di antaranya sedang disembunyikan filter
          </span>
        )}
      </p>
      <div className="flex items-center gap-2 ml-auto">
        {children}
        <Button variant="ghost" size="sm" onClick={onClear}>
          Batal pilih
        </Button>
      </div>
    </div>
  );
}

/**
 * The report after a bulk permanent delete. What was not deleted matters as
 * much as what was: the in-use rows stay ticked, and saying so is what turns
 * "it skipped some" into a next step. `usedWhere` finishes the sentence about
 * why ('event, promo, atau transaksi'); the deactivate hint is only offered to
 * someone who can act on it.
 */
export function describeBulkDelete(
  r: BulkHardDeleteResult,
  noun: string,
  usedWhere: string,
  canDeactivate: boolean,
): string {
  const lines = [`${r.deleted.length} ${noun} dihapus permanen.`];
  if (r.inUse.length > 0) {
    lines.push(
      `${r.inUse.length} ${noun} tidak dihapus karena sudah pernah dipakai di ${usedWhere}.` +
        (canDeactivate ? ' Semuanya masih tercentang — klik Nonaktifkan untuk menyembunyikannya.' : ''),
    );
  }
  if (r.untouched.length > 0) {
    lines.push(
      `${r.untouched.length} ${noun} tidak terhapus: izin tidak cukup, atau sudah lebih dulu dihapus orang lain.`,
    );
  }
  return lines.join('\n\n');
}
